import React, { memo, useContext, useEffect, useRef, Fragment } from 'react';

import { ContextPanel } from '@atlaskit/editor-core';

import {
	useIsObjectSidebarShown,
	usePanelWidth,
	usePanel,
	MIN_PANEL_WIDTH,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { isMigratedToPanelSystem } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { ApprovalsPane } from '@confluence/approvals/entry-points/ApprovalsPane.ts';
import {
	APPROVALS_PANE_ID,
	APPROVALS_PANE_WIDTH,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { confluenceSessionStorageInstance as sessionStorageManager } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const APPROVALS_PANE_DRAFT_KEY = 'confluence.approvals.pane.draftShareId';

export const EditorContextPanelView = memo(() => {
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const panelWidth = usePanelWidth();
	const panel = usePanel();

	const { item: contextPanelItem, editorAPI } = useContext(EditorContextPanelContext);

	const rightSidebarContext = useRightSidebarContext();
	const isSidebarApprovalsPaneOpen =
		rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID;
	const routeDataRef = useRouteDataRef();

	// Detect stale approval pane synchronously during render to avoid flicker.
	// When the pane opens we store the current URL in session storage. If the
	// sidebar says the pane is open but the stored URL doesn't match, the pane
	// belongs to a previous page and should not be rendered.
	let isApprovalsPaneOpen =
		isSidebarApprovalsPaneOpen && expValEquals('confluence_approvals_eap', 'isEnabled', true);

	if (isApprovalsPaneOpen) {
		const currentUrl = routeDataRef?.current?.href;
		const storedUrl = sessionStorageManager.getItem(APPROVALS_PANE_DRAFT_KEY);
		if (storedUrl && storedUrl !== currentUrl) {
			// Stale pane from a different page — suppress rendering
			isApprovalsPaneOpen = false;
		} else if (!storedUrl) {
			// First render after the pane was opened — record the URL
			sessionStorageManager.setItem(APPROVALS_PANE_DRAFT_KEY, currentUrl);
		}
	}

	// Keep the sidebar context and session storage in sync asynchronously.
	const rightSidebarContextRef = useRef(rightSidebarContext);
	rightSidebarContextRef.current = rightSidebarContext;

	useEffect(() => {
		if (!expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
			return;
		}

		if (isSidebarApprovalsPaneOpen) {
			const currentUrl = routeDataRef?.current?.href;
			const storedUrl = sessionStorageManager.getItem(APPROVALS_PANE_DRAFT_KEY);
			if (storedUrl && storedUrl !== currentUrl) {
				// The pane was opened on a different page — close the sidebar context
				rightSidebarContextRef.current.close(APPROVALS_PANE_ID);
				sessionStorageManager.removeItem(APPROVALS_PANE_DRAFT_KEY);
			} else {
				sessionStorageManager.setItem(APPROVALS_PANE_DRAFT_KEY, currentUrl);
			}
		} else {
			// Pane is closed — clean up
			sessionStorageManager.removeItem(APPROVALS_PANE_DRAFT_KEY);
		}
	}, [isSidebarApprovalsPaneOpen, routeDataRef]);

	if (isApprovalsPaneOpen) {
		return (
			<ContextPanel
				visible
				editorAPI={editorAPI ?? undefined}
				hasPadding={false}
				customWidth={APPROVALS_PANE_WIDTH}
			>
				<ApprovalsPane />
			</ContextPanel>
		);
	}

	// Previously, the pattern was to set state in EditorContextPanelContext in order to render components here.
	// Now, we render components directly in EditorContextPanelView to avoid bugs like https://hello.jira.atlassian.cloud/browse/DISCO-4183
	//
	// When the panel system flag is on, comments/details/jira panels render via navigation-system Panel in
	// PageLayout.tsx. Extension panels (ToC, etc.) always render here in ContextPanel.
	if (
		isObjectSidebarShown &&
		(fg('confluence_frontend_content_wrapper') || isRovoButtonEnabled) &&
		!(fg('ads-panel-system-confluence-object-sidebar') && isMigratedToPanelSystem(panel?.id))
	) {
		return (
			<ContextPanel
				visible
				editorAPI={editorAPI ?? undefined}
				hasPadding={false}
				customWidth={
					(panel?.id === 'proactive-ai-panel' ||
						(panel?.id.startsWith && panel?.id.startsWith('extensions-panel')) ||
						panel?.id === 'editor-element-insert-sidebar-panel') &&
					fg('platform_editor_ai_object_sidebar_injection')
						? panelWidth || MIN_PANEL_WIDTH
						: MIN_PANEL_WIDTH
				}
			>
				<ObjectSidebarPanel />
			</ContextPanel>
		);
	}

	let component;
	let hasPadding;
	let customWidth;

	if (contextPanelItem) {
		component = contextPanelItem.component;
		hasPadding = contextPanelItem.hasPadding;
		customWidth = contextPanelItem.customWidth;
	}

	return (
		<ContextPanel
			visible={!!contextPanelItem}
			editorAPI={editorAPI ?? undefined}
			hasPadding={hasPadding}
			customWidth={customWidth}
		>
			<Fragment>{component}</Fragment>
		</ContextPanel>
	);
});
